import type { ProjectsResponse, Subproject } from '../../../api/projects-client/projects-client.type';
import type { Epic } from '../../../api/tasks-client/task-client.type';
import type { Workflow } from '../../../api/workflows-client/workflows-client.type';
import i18n from '../../../base-dictionary';
import type { FilterGroup } from '../../../components/filters/filter-panel.type';
import { snakeCaseToTitleCase } from '../../../helpers/string-helpers/string-helpers';
import { TaskType } from '../../tasks-table/tasks-table.type';
import type { ProcessScope } from '../context/process.context.type';

/**
 * Creates filter groups for a process section (left or right)
 *
 * @param params Configuration parameters for creating filter groups
 * @returns Array of filter groups
 */
const createProcessFilterGroups = (params: {
  teams: ProjectsResponse[];
  team: ProjectsResponse | null;
  board: Subproject | null;
  taskType: string | null;
  taskTypes: string[];
  epic: Epic | null;
  epics: Epic[];
  useWorkflow: boolean;
  workflow: Workflow | null;
  workflows: Workflow[];
  allSeenTaskTypesRef: React.MutableRefObject<Set<string>>;
  onTeamSelected: (team: ProjectsResponse) => void;
  onBoardSelected: (board: Subproject | null) => void;
  onTaskTypeSelected: (taskType: string | null) => void;
  onEpicSelected: (epic: Epic | null) => void;
  onWorkflowChange: (workflowId: string | null) => void;
}): FilterGroup[] => {
  const {
    teams,
    team,
    board,
    taskType,
    taskTypes,
    epic,
    epics,
    useWorkflow,
    workflow,
    workflows,
    allSeenTaskTypesRef,

    onTeamSelected,
    onBoardSelected,
    onTaskTypeSelected,
    onEpicSelected,
    onWorkflowChange,
  } = params;

  const groups: FilterGroup[] = [];

  // Team filter group
  if (!useWorkflow) {
    const teamFilterGroup: FilterGroup = {
      name: i18n.t('common.project'),
      options: teams.map((t) => ({ value: t.id, label: t.name })),
      selectedValues: team ? [team.id] : [],
      optional: false, // Make it required
      onSelectionChange: (values) => {
        if (values.length > 0) {
          const selectedTeam = teams.find((t) => t.id === values[0]);
          if (selectedTeam) {
            onTeamSelected({ ...selectedTeam });
          } else if (teams.length > 0) {
            // If selection is invalid but teams exist, select the first team
            onTeamSelected({ ...teams[0] });
          }
        } else if (teams.length > 0) {
          // Don't allow deselection - reselect the current team or first team
          const teamToSelect = team || teams[0];
          onTeamSelected({ ...teamToSelect });
        }
      },
    };
    groups.push(teamFilterGroup);
  } else {
    const workflowFilterGroup: FilterGroup = {
      name: i18n.t('data_management.workflows.workflow'),
      options: workflows?.map((w) => ({ value: w.id, label: w.name })) || [],
      selectedValues: workflow ? [workflow.id] : [],
      optional: false, // Make it required (already was, but being explicit)
      onSelectionChange: (values) => {
        if (values.length > 0) {
          onWorkflowChange(values[0] as string);
        } else if (workflows && workflows.length > 0) {
          // Don't allow deselection - reselect the current workflow or first workflow
          const workflowToSelect = workflow?.id || workflows[0].id;
          onWorkflowChange(workflowToSelect);
        }
      },
    };
    groups.push(workflowFilterGroup);
  }

  // Create board option objects for the select
  const boardOptionsForSelect =
    useWorkflow && workflow
      ? getWorkflowBoardOptions(teams, workflow).map((b) => ({ value: b.id, label: b.name }))
      : team
        ? team.subprojects?.map((b) => ({ value: b.id, label: b.name })) || []
        : [];

  // Board filter group - use different boards based on whether workflow is active
  const boardFilterGroup: FilterGroup = {
    name: i18n.t('common.board'),
    options: boardOptionsForSelect,
    optional: true,
    selectedValues: board ? [board.id] : [],
    onSelectionChange: (values) => {
      if (values.length > 0) {
        // Find the selected board from the appropriate source
        const boardsToSearchIn =
          useWorkflow && workflow ? getWorkflowBoardOptions(teams, workflow) : team?.subprojects || [];

        const selectedBoard = boardsToSearchIn.find((b) => b.id === values[0]);

        if (selectedBoard) {
          onBoardSelected(selectedBoard);
        } else {
          onBoardSelected(null);
        }
      } else {
        onBoardSelected(null);
      }
    },
  };
  groups.push(boardFilterGroup);

  // Update seen task types with new ones from taskTypes array
  if (taskTypes?.length) {
    taskTypes.forEach((type) => {
      if (!allSeenTaskTypesRef.current.has(type)) {
        allSeenTaskTypesRef.current.add(type);
      }
    });
  }

  // Get task types from the cached values and fall back to the enum
  const customTaskType = taskType && !Object.values(TaskType).includes(taskType as TaskType) ? taskType : null;
  const taskTypeOptions = Array.from(allSeenTaskTypesRef.current).map((type) => ({
    value: type,
    label: snakeCaseToTitleCase(type),
  }));

  // If there's a custom task type selected that's not in the options, add it
  if (customTaskType && !taskTypeOptions.some((option) => option.value === customTaskType)) {
    taskTypeOptions.push({
      value: customTaskType,
      label: snakeCaseToTitleCase(customTaskType as string),
    });
    allSeenTaskTypesRef.current.add(customTaskType);
  }

  // Task Type filter group
  const taskTypeFilterGroup: FilterGroup = {
    name: `${i18n.t('common.work_unit')} Type`,
    options: taskTypeOptions,
    optional: true,
    selectedValues: taskType ? [taskType] : [],
    onSelectionChange: (values) => {
      onTaskTypeSelected(values.length > 0 ? values[0] : null);
    },
  };
  groups.push(taskTypeFilterGroup);

  if (!useWorkflow) {
    const epicFilterGroup: FilterGroup = {
      name: i18n.t('common.parent_task'),
      options: epics.map((e) => ({ value: e.id, label: e.name })),
      optional: true,
      selectedValues: epic ? [epic.id] : [],
      onSelectionChange: (values) => {
        if (values.length > 0) {
          const selectedEpic = epics.find((e) => e.id === values[0]);
          if (selectedEpic) {
            onEpicSelected({ ...selectedEpic });
          } else {
            onEpicSelected(null);
          }
        } else {
          onEpicSelected(null);
        }
      },
    };
    groups.push(epicFilterGroup);
  }

  return groups;
};

/**
 * Helper function to calculate workflow board options
 *
 * @param teams All available teams
 * @param workflow Current workflow
 * @returns Board options for the current workflow
 */
const getWorkflowBoardOptions = (teams: ProjectsResponse[], workflow: Workflow): Subproject[] => {
  if (!workflow) return [];

  const options: Subproject[] = [];

  teams.forEach((team) => {
    if (team.subprojects) {
      team.subprojects.forEach((subproject) => {
        if (workflow.subprojects.includes(subproject.id)) {
          options.push(subproject);
        }
      });
    }
  });

  return options;
};

/**
 * Creates a scope update function that works with handleWorkflowSelected
 *
 * @param setScope The scope setter function
 * @param workflows Available workflows
 * @param teams Available teams
 * @returns A function that handles workflow toggle selection
 */
const createWorkflowSelectHandler = (
  setScope: (scope: Partial<ProcessScope>) => void,
  workflows: Workflow[],
  teams: ProjectsResponse[],
) => {
  return (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.currentTarget.checked;

    if (isChecked) {
      // Toggle ON - First clear team, board, and epic, then set workflow
      setScope({
        team: null,
        board: null,
        epic: null,
        useWorkflow: true,
      });

      // Then select the first workflow in a separate update
      setTimeout(() => {
        const firstWorkflow = workflows && workflows.length > 0 ? workflows[0] : null;
        if (firstWorkflow) {
          setScope({ workflow: firstWorkflow });
        }
      }, 0);
    } else {
      // Toggle OFF - First clear workflow and set useWorkflow false
      setScope({
        workflow: null,
        board: null,
        epic: null,
        useWorkflow: false,
        useGroups: true,
      });

      // Then select the first team in a separate update
      setTimeout(() => {
        const firstTeam = teams && teams.length > 0 ? teams[0] : null;
        if (firstTeam) {
          setScope({ team: firstTeam });
        }
      }, 0);
    }
  };
};

/**
 * Creates a handler for resetting filters
 *
 * @param setScope The scope setter function
 * @param useWorkflow Whether workflow view is active
 * @param workflows Available workflows
 * @param teams Available teams
 * @returns A function that handles resetting filters
 */
const createResetFiltersHandler = (
  setScope: (scope: Partial<ProcessScope>) => void,
  useWorkflow: boolean,
  workflows: Workflow[],
  teams: ProjectsResponse[],
) => {
  return () => {
    // When resetting filters, still maintain team or workflow selection
    if (useWorkflow) {
      const firstWorkflow = workflows && workflows.length > 0 ? workflows[0] : null;
      setScope({
        taskType: null,
        epic: null,
        board: null,
        workflow: firstWorkflow,
        useWorkflow: true,
      });
    } else {
      const firstTeam = teams && teams.length > 0 ? teams[0] : null;
      setScope({
        taskType: null,
        epic: null,
        board: null,
        workflow: null,
        useWorkflow: false,
        useGroups: true,
        team: firstTeam,
      });
    }
  };
};

export { createProcessFilterGroups, createResetFiltersHandler, createWorkflowSelectHandler };
